import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Image as ImageIcon, Loader2, Save, AlertCircle, CheckCircle2, Upload, Sparkles,
  RotateCcw, FileCode, Monitor, ExternalLink, Info, ChevronDown
} from 'lucide-react'
import { ApiService } from '@/services/api'

type BannerMode = 'url' | 'svg'
type PreviewTab = 'new' | 'old'

const SVG_PATTERN = /<svg[\s>]/i
const MAX_SVG_BYTES = 300000
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3008/api'

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

const formatKb = (bytes: number) => `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`

// Sandboxed iframe so animated SVGs (CSS animations, web fonts) render faithfully without running scripts.
// "slice" makes it fill the frame the same way the app's login panel does.
function SvgPreview({ svg }: { svg: string }) {
  const sized = /preserveAspectRatio=/i.test(svg)
    ? svg
    : svg.replace(/<svg\b/i, '<svg preserveAspectRatio="xMidYMid slice"')
  const doc = `<!doctype html><html><head><style>html,body{margin:0;height:100%;overflow:hidden;background:transparent}svg{display:block;width:100%;height:100%}</style></head><body>${sized}</body></html>`
  return <iframe title="SVG banner preview" sandbox="" srcDoc={doc} className="w-full h-full border-0 block" />
}

// Mimics the login screen's left panel so the admin sees the banner in context
function PreviewFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-muted/40 p-3">
      <div className="flex items-center gap-1.5 mb-2 px-1">
        <span className="h-2 w-2 rounded-full bg-red-300" />
        <span className="h-2 w-2 rounded-full bg-amber-300" />
        <span className="h-2 w-2 rounded-full bg-green-300" />
        <span className="ml-2 text-[11px] text-muted-foreground">Restaurant app · Login screen</span>
      </div>
      <div className="mx-auto w-full max-w-[340px] aspect-[1167/1360] rounded-2xl overflow-hidden bg-card border shadow-sm">
        {children}
      </div>
    </div>
  )
}

function EmptyPreview({ label }: { label: string }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center text-center gap-2 p-6 text-muted-foreground">
      <ImageIcon className="h-8 w-8 opacity-50" />
      <p className="text-sm">{label}</p>
    </div>
  )
}

export default function BannerManagementScreen() {
  // Saved (live) state
  const [savedSvg, setSavedSvg] = useState<string>('')
  const [savedUrl, setSavedUrl] = useState<string>('')

  // Draft state
  const [mode, setMode] = useState<BannerMode>('url')
  const [svgCode, setSvgCode] = useState<string>('')
  const [bannerUrl, setBannerUrl] = useState<string>('')

  const [previewTab, setPreviewTab] = useState<PreviewTab>('new')
  const [showCode, setShowCode] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetchCurrentBanner()
  }, [])

  const fetchCurrentBanner = async () => {
    setIsFetching(true)
    try {
      const response = await fetch(`${API_BASE}/banner`)
      const data = await response.json()
      if (data.success) {
        const svg: string = data.data.bannerSvg || ''
        const url: string = data.data.bannerImageUrl || ''
        setSavedSvg(svg)
        setSavedUrl(url)
        setSvgCode(svg)
        setBannerUrl(url)
        setMode(svg ? 'svg' : 'url')
      }
    } catch (err) {
      console.error('Failed to fetch banner:', err)
    } finally {
      setIsFetching(false)
    }
  }

  const svgBytes = useMemo(() => new Blob([svgCode]).size, [svgCode])
  const svgValid = SVG_PATTERN.test(svgCode)
  const svgTooBig = svgBytes > MAX_SVG_BYTES
  const urlValid = isValidUrl(bannerUrl)

  const savedMode: BannerMode | null = savedSvg ? 'svg' : savedUrl ? 'url' : null
  const isDirty = mode === 'svg'
    ? svgCode !== savedSvg || bannerUrl !== savedUrl || savedMode !== 'svg'
    : bannerUrl !== savedUrl || savedMode !== 'url'

  const canSave = mode === 'svg'
    ? svgValid && !svgTooBig && (!bannerUrl.trim() || urlValid)
    : urlValid

  const loadSvgFile = async (file?: File | null) => {
    if (!file) return
    setError(null)
    if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') {
      setError('Please choose an .svg file')
      return
    }
    setSvgCode(await file.text())
    setPreviewTab('new')
  }

  const handleDiscard = () => {
    setSvgCode(savedSvg)
    setBannerUrl(savedUrl)
    setMode(savedMode ?? 'url')
    setError(null)
  }

  const handleSave = async () => {
    setError(null)
    setSuccess(null)

    if (mode === 'svg') {
      if (!svgValid) return setError('That doesn’t look like SVG code — it must contain an <svg> element')
      if (svgTooBig) return setError('SVG is too large (max 300 KB)')
      if (bannerUrl.trim() && !urlValid) return setError('Fallback image URL is not a valid URL')
    } else if (!urlValid) {
      return setError('Please enter a valid image URL')
    }

    setIsLoading(true)
    try {
      const response = await ApiService.updateBanner(
        mode === 'svg'
          ? { bannerSvg: svgCode, bannerImageUrl: bannerUrl.trim() || undefined }
          : { bannerImageUrl: bannerUrl }
      )
      if (response.success) {
        setSavedSvg(mode === 'svg' ? svgCode : '')
        setSavedUrl(bannerUrl.trim())
        if (mode === 'url') setSvgCode('')
        setSuccess('Banner published — apps will pick it up within the hour.')
        setTimeout(() => setSuccess(null), 5000)
      } else {
        setError(response.message || 'Failed to update banner')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update banner. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isFetching) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // What the preview should show for each app generation
  const newAppSvg = mode === 'svg' && svgValid ? svgCode : null
  const fallbackUrl = bannerUrl.trim() && urlValid ? bannerUrl : null
  const showTabs = mode === 'svg'
  const activeTab: PreviewTab = showTabs ? previewTab : 'old'

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto pb-0">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Banner</h1>
            <p className="text-sm text-muted-foreground">
              The artwork shown beside the login form in the restaurant app.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Live now:</span>
          {savedMode === 'svg' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 px-2.5 py-1 font-medium">
              <Sparkles className="h-3 w-3" /> Animated SVG
            </span>
          ) : savedMode === 'url' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-2.5 py-1 font-medium">
              <ImageIcon className="h-3 w-3" /> Static image
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 font-medium">
              Default artwork
            </span>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] items-start">
        {/* ───────── Editor ───────── */}
        <div className="space-y-6">
          {/* Step 1: choose type */}
          <section>
            <h2 className="text-sm font-semibold mb-2">1 · Choose banner type</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {([
                { m: 'svg', icon: Sparkles, title: 'Animated SVG', desc: 'Motion and branding. Newer app versions animate it; older ones show a fallback image.' },
                { m: 'url', icon: ImageIcon, title: 'Static image', desc: 'A single image from a URL. Works in every app version.' }
              ] as const).map(({ m, icon: Icon, title, desc }) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); setError(null) }}
                  aria-pressed={mode === m}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    mode === m ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/50' : 'hover:border-foreground/30 bg-card'
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    <Icon className={`h-4 w-4 ${mode === m ? 'text-blue-600' : 'text-muted-foreground'}`} />
                    {title}
                    {mode === m && <CheckCircle2 className="h-4 w-4 text-blue-600 ml-auto" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">{desc}</p>
                </button>
              ))}
            </div>
          </section>

          {/* Step 2: content */}
          <section>
            <h2 className="text-sm font-semibold mb-2">2 · {mode === 'svg' ? 'Add your SVG' : 'Add your image'}</h2>
            <Card className="p-5 space-y-5">
              {mode === 'svg' ? (
                <>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); loadSvgFile(e.dataTransfer.files?.[0]) }}
                    onClick={() => fileInput.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
                    className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                      dragging ? 'border-blue-500 bg-blue-50' : 'hover:border-foreground/40 bg-muted/30'
                    }`}
                  >
                    <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm font-medium">Drop an .svg file here, or click to browse</p>
                    <p className="text-xs text-muted-foreground mt-1">CSS animations and web fonts are supported · max 300 KB</p>
                    <input
                      ref={fileInput}
                      type="file"
                      accept=".svg,image/svg+xml"
                      className="hidden"
                      onChange={(e) => { loadSvgFile(e.target.files?.[0]); e.target.value = '' }}
                    />
                  </div>

                  {svgCode.trim() && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs rounded-lg bg-muted/50 px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 font-medium">
                        <FileCode className="h-3.5 w-3.5" /> SVG loaded
                      </span>
                      <span className={svgTooBig ? 'text-destructive' : 'text-muted-foreground'}>{formatKb(svgBytes)} / 300 KB</span>
                      {svgValid ? (
                        <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Valid</span>
                      ) : (
                        <span className="text-destructive inline-flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> No &lt;svg&gt; element found</span>
                      )}
                      <button type="button" className="ml-auto text-destructive hover:underline" onClick={() => setSvgCode('')}>
                        Remove
                      </button>
                    </div>
                  )}

                  <div>
                    <button
                      type="button"
                      onClick={() => setShowCode(v => !v)}
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${showCode ? 'rotate-180' : ''}`} />
                      {showCode ? 'Hide code' : 'Paste or edit code'}
                    </button>
                    {showCode && (
                      <textarea
                        value={svgCode}
                        onChange={(e) => setSvgCode(e.target.value)}
                        placeholder={'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1167 1360">…</svg>'}
                        rows={10}
                        spellCheck={false}
                        aria-label="SVG code"
                        className="mt-2 w-full rounded-md border bg-background px-3 py-2 text-xs font-mono"
                      />
                    )}
                  </div>

                  <div className="border-t pt-5 space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="fallbackUrl">Fallback image URL</Label>
                      <span className="text-[11px] rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 font-medium">Recommended</span>
                    </div>
                    <Input
                      id="fallbackUrl"
                      type="url"
                      value={bannerUrl}
                      onChange={(e) => setBannerUrl(e.target.value)}
                      placeholder="https://res.cloudinary.com/…"
                    />
                    <p className="text-xs text-muted-foreground">
                      Older app versions can’t play SVG. Without a fallback they’ll keep showing the default artwork.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="bannerUrl">Image URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="bannerUrl"
                      type="url"
                      value={bannerUrl}
                      onChange={(e) => setBannerUrl(e.target.value)}
                      placeholder="https://res.cloudinary.com/…"
                      className="flex-1"
                    />
                    {urlValid && (
                      <Button type="button" variant="outline" size="icon" title="Open in new tab" onClick={() => window.open(bannerUrl, '_blank')}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use a portrait image (about 6:7) so it fills the login panel without heavy cropping.
                  </p>
                </div>
              )}
            </Card>
          </section>

          {/* Compatibility info */}
          <div className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
            <div className="space-y-1">
              <p className="font-medium">How apps receive it</p>
              <p className="text-blue-800/90 text-xs">
                Apps fetch <code className="bg-blue-100 px-1 rounded">GET {API_BASE}/banner</code> and cache the result for an hour.
                The response carries <code className="bg-blue-100 px-1 rounded">bannerSvg</code> and/or{' '}
                <code className="bg-blue-100 px-1 rounded">bannerImageUrl</code>; apps that support SVG prefer it.
                Scripts and event handlers are stripped from SVGs on save.
              </p>
            </div>
          </div>
        </div>

        {/* ───────── Live preview ───────── */}
        <aside className="lg:sticky lg:top-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Monitor className="h-4 w-4 text-muted-foreground" /> Live preview
            </h2>
            {isDirty && (
              <span className="text-[11px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-medium">Not published</span>
            )}
          </div>

          {showTabs && (
            <div className="grid grid-cols-2 rounded-lg border bg-muted/50 p-1 text-xs">
              {([['new', 'Newer apps'], ['old', 'Older apps']] as const).map(([t, label]) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPreviewTab(t)}
                  className={`rounded-md py-1.5 font-medium transition-colors ${
                    activeTab === t ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <PreviewFrame>
            {activeTab === 'new' && newAppSvg ? (
              <SvgPreview svg={newAppSvg} />
            ) : activeTab === 'new' ? (
              <EmptyPreview label="Add valid SVG code to preview the animation" />
            ) : fallbackUrl ? (
              <img
                key={fallbackUrl}
                src={fallbackUrl}
                alt="Banner preview"
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <EmptyPreview label={mode === 'svg' ? 'No fallback — older apps show the default artwork' : 'Enter an image URL to preview'} />
            )}
          </PreviewFrame>

          <p className="text-[11px] text-muted-foreground text-center">
            {showTabs
              ? activeTab === 'new' ? 'What up-to-date apps will show.' : 'What apps without SVG support will show.'
              : 'Shown in all app versions.'}
          </p>
        </aside>
      </div>

      {/* ───────── Sticky action bar ───────── */}
      <div className="sticky bottom-0 z-30 mt-8 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
          <p className="text-sm flex-1 min-w-0 truncate">
            {isDirty ? (
              <span className="inline-flex items-center gap-2 text-amber-700">
                <span className="h-2 w-2 rounded-full bg-amber-500" /> Unsaved changes
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-green-500" /> Everything is published
              </span>
            )}
          </p>
          <Button variant="ghost" onClick={handleDiscard} disabled={!isDirty || isLoading}>
            <RotateCcw className="mr-2 h-4 w-4" /> Discard
          </Button>
          <Button onClick={handleSave} disabled={!isDirty || !canSave || isLoading}>
            {isLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Publishing…</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Publish banner</>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
